import React, { useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../store/store";
import { Modal } from "react-bootstrap";
import { ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { Chamado } from "../../types/types";
import Loading from "../../components/Loading";
import { useSessionExpirationCheck } from "../../hooks/useSessionExpirationCheck";

const Chamados = () => {
  const [chamados, setChamados] = useState<Chamado[] | string>([]);
  const [modalShow, setModalShow] = useState(false);
  const [modalRelatoriosShow, setModalRelatoriosShow] = useState(false);

  const [modalUserShow, setModalUserShow] = useState(false);
  const [resposta, setResposta] = useState("");
  const [chamadoAtual, setChamadoAtual] = useState<Chamado | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const token = useSelector((state: RootState) => state.auth.token);
  const usuario = useSelector((state: RootState) => state.auth.user);
  const sessionExpiration = useSelector(
    (state: RootState) => state.auth.SessionExpiration,
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useSessionExpirationCheck(sessionExpiration, token);

  const handleLoadChamados = useCallback(async () => {
    if (!usuario) return;

    try {
      let url = "";

      if (usuario.nivel !== "1") {
        url = `${process.env.REACT_APP_API}/GetAllByUserId?id=${usuario.id}`;
      } else {
        url = `${process.env.REACT_APP_API}/api/chamado`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error(errorData);
        throw new Error(errorData.mensagem);
      }

      const data = await response.json();
      setChamados(data);
    } catch (error) {
      if (error instanceof Error) {
        console.error("Erro ao buscar chamados:", error.message);
        setChamados("Houve um erro ao buscar chamados");
      }
    }
  }, [token, usuario]);

  useEffect(() => {
    if (!token) {
      navigate("/area-do-cliente");
      return;
    }

    handleLoadChamados();
    document.title = "Íkone | Chamados";
  }, [token, navigate, handleLoadChamados, sessionExpiration, dispatch]);

  const handleResponderChamado = async () => {
    if (chamadoAtual?.status === "Encerrado") {
      toast.error("Não é possível responder um chamado encerrado.");
      return;
    }
    if (!resposta.trim()) {
      toast.error("A resposta não pode estar vazia.");
      return;
    }
    try {
      const corpoRequisicao = {
        ID_CHAMADO: chamadoAtual?.uuid,
        TEXTO_RESPOSTA: resposta,
        DATA_RESPOSTA: new Date().toISOString(),
        ID_USUARIO_RESPOSTA: usuario?.id,
      };
      const response = await fetch(
        `${process.env.REACT_APP_API}/api/Chamado/${chamadoAtual?.uuid}/responder`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(corpoRequisicao),
        },
      );

      if (response.ok) {
        toast.success("Resposta enviada com sucesso.");
        const novaRespostaCriada = await response.json();
        setResposta("");

        setChamados((prevChamados) =>
          Array.isArray(prevChamados)
            ? prevChamados.map((c) =>
                chamadoAtual && c.uuid === chamadoAtual.uuid
                  ? {
                      ...c,
                      status: "Respondido",
                      respostas: [...c.respostas, novaRespostaCriada],
                    }
                  : c,
              )
            : prevChamados,
        );

        setChamadoAtual((prevChamado) => {
          if (!prevChamado) {
            return null;
          }
          return {
            ...prevChamado,
            status: "Respondido",
            respostas: [...prevChamado.respostas, novaRespostaCriada],
          };
        });
      } else {
        const errorData = await response.json();
        console.error(errorData.errors[0]);
      }
    } catch (error) {
      if (error instanceof Error) {
        console.error("Erro ao responder chamado:", error.message);
      }
    }
  };

  const handleDownload = async (caminho: string, nomeArquivo: string) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${
          process.env.REACT_APP_API
        }/api/chamado/DownloadRelatorio?caminho=${encodeURIComponent(caminho)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) throw new Error("Erro ao baixar o arquivo");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = nomeArquivo;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      }
    }
  };

  const handleUploadChamadoRelatorio = async (
    chamadoAtual: Chamado,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];

    if (!file) {
      toast.error("Nenhum arquivo selecionado.");
      return;
    }

    const formData = new FormData();
    formData.append("chamado_id", chamadoAtual.uuid);
    formData.append("enviador", String(usuario?.login));
    formData.append("relatorio", file);

    setIsLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API}/relatorio`, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        toast.success("Relatório enviado com sucesso.");
      } else {
        toast.error("Erro ao enviar relatório.");
      }
    } catch (error) {
      if (error instanceof Error) {
        console.error("Erro:", error.message);
      }
    } finally {
      event.target.value = "";
      setIsLoading(false);
    }
  };

  if (usuario?.nivel !== "1") {
    return (
      <div className="container mt-5">
        <div className="d-flex align-items-center">
          <ChevronLeft
            size={23}
            className="me-2"
            style={{ cursor: "pointer" }}
            onClick={() => navigate(-1)}
          />
          <h2 className="text-left text-primary fw-bold">Meus de Chamados</h2>
        </div>

        <div className="d-flex justify-content-center justify-content-md-start justify-content-lg-start justify-content-xl-start flex-wrap gap-3 mt-4">
          {Array.isArray(chamados) ? (
            <>
              {[...chamados]
                .sort((a, b) => {
                  const statusOrder: { [key: string]: number } = {
                    Pendente: 1,
                    Respondido: 2,
                    Encerrado: 3,
                  };

                  const statusA = statusOrder[a.status] || 4;
                  const statusB = statusOrder[b.status] || 4;

                  if (statusA !== statusB) {
                    return statusA - statusB;
                  }

                  return (
                    new Date(b.horariO_CRIADO).getTime() -
                    new Date(a.horariO_CRIADO).getTime()
                  );
                })
                .map((chamado) => (
                  <div
                    key={chamado.uuid}
                    className="card d-flex flex-column justify-content-between shadow-sm border-0 rounded-4 d-flex"
                    style={{
                      width: "19rem",
                      height: "100%",
                      backgroundColor: "#f8f9fa",
                      padding: "10px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <div className="d-flex mb-3 justify-content-between">
                      {chamado.num_Chamado !== "" ? (
                        <div className="d-flex flex-column">
                          <span className="fw-semibold">Chamado:</span>
                          <span className="mb-2 text-sm">
                            {chamado.num_Chamado !== ""
                              ? `#${chamado.num_Chamado}`
                              : "N/A"}
                          </span>

                          <span className="fw-semibold">Criado em:</span>
                          <span className="text-sm">
                            {chamado?.horariO_CRIADO
                              ? new Date(chamado.horariO_CRIADO)
                                  .toLocaleString("pt-BR", {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })
                                  .replace(",", " às")
                              : "N/A"}
                          </span>
                        </div>
                      ) : (
                        <div className="d-flex flex-column">
                          <span className="fw-semibold">Chamado:</span>
                          <span className="mb-2 text-sm">
                            {chamado.num_Chamado !== ""
                              ? `#${chamado.num_Chamado}`
                              : "N/A"}
                          </span>

                          <span className="fw-semibold">Criado em:</span>
                          <span className="text-sm">
                            {chamado?.horariO_CRIADO
                              ? new Date(chamado.horariO_CRIADO)
                                  .toLocaleString("pt-BR", {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })
                                  .replace(",", " às")
                              : "N/A"}
                          </span>
                        </div>
                      )}
                      <span>
                        {chamado.status === "Pendente" && (
                          <div
                            className="d-flex align-items-center gap-2 w-100 justify-content-center"
                            style={{
                              backgroundColor: "#f3f3d9",
                              padding: "8px",
                              borderRadius: "8px",
                            }}
                          >
                            <span
                              style={{
                                background: "orange",
                                width: "10px",
                                height: "10px",
                                borderRadius: "50%",
                              }}
                            ></span>
                            <span className="fw-semibold text-warning">
                              Pendente
                            </span>
                          </div>
                        )}
                        {chamado.status === "Respondido" && (
                          <div
                            className="d-flex align-items-center gap-2 w-100 justify-content-center"
                            style={{
                              backgroundColor: "#d4edda",
                              padding: "8px",
                              borderRadius: "8px",
                            }}
                          >
                            <span
                              style={{
                                background: "limegreen",
                                width: "10px",
                                height: "10px",
                                borderRadius: "50%",
                              }}
                            ></span>
                            <span className="fw-semibold text-success">
                              Respondido
                            </span>
                          </div>
                        )}
                        {chamado.status === "Encerrado" && (
                          <div
                            className="d-flex align-items-center gap-2 w-100 justify-content-center"
                            style={{
                              backgroundColor: "#edd4d4",
                              padding: "8px",
                              borderRadius: "8px",
                            }}
                          >
                            <span
                              style={{
                                background: "red",
                                width: "10px",
                                height: "10px",
                                borderRadius: "50%",
                              }}
                            ></span>
                            <span className="fw-semibold text-danger">
                              Encerrado
                            </span>
                          </div>
                        )}
                      </span>
                    </div>
                    <button
                      className="btn btn-primary"
                      onClick={() => {
                        setModalUserShow(true);
                        setChamadoAtual(chamado);
                      }}
                    >
                      Acessar
                    </button>
                  </div>
                ))}

              <div
                className="card shadow-sm border-0 rounded-4 d-flex align-items-center justify-content-center text-primary fw-bold"
                style={{
                  width: "19rem",
                  height: "8rem",
                  padding: "10px",
                  whiteSpace: "nowrap",
                  cursor: "pointer",
                  transition: "0.3s",
                }}
                onClick={() => (window.location.href = "/chamado")}
                onMouseEnter={(e) => e.currentTarget.classList.add("shadow")}
                onMouseLeave={(e) => e.currentTarget.classList.remove("shadow")}
              >
                + Novo Chamado
              </div>
            </>
          ) : (
            <div className="d-flex justify-content-center w-100">
              <div
                className="card shadow-sm border-0 rounded-4 d-flex align-items-center justify-content-center text-primary fw-bold"
                style={{
                  width: "19rem",
                  height: "8rem",
                  padding: "10px",
                  whiteSpace: "nowrap",
                  cursor: "pointer",
                  transition: "0.3s",
                }}
                onClick={() => (window.location.href = "/chamado")}
                onMouseEnter={(e) => e.currentTarget.classList.add("shadow")}
                onMouseLeave={(e) => e.currentTarget.classList.remove("shadow")}
              >
                + Novo Chamado
              </div>
            </div>
          )}
        </div>
        <Modal
          show={modalUserShow}
          onHide={() => setModalUserShow(false)}
          centered
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <div className="d-flex flex-column">
                <strong>Informações do Chamado</strong>
                <div className="d-flex align-items-center gap-3 mt-1 text-muted fs-6">
                  <span>
                    {chamadoAtual?.num_Chamado !== "" ? (
                      <div className="d-flex gap-1">
                        <strong>Chamado:</strong>
                        <span className="text-secondary">
                          #{chamadoAtual?.num_Chamado}
                        </span>
                      </div>
                    ) : (
                      <div className="d-flex gap-1">
                        <strong>Chamado:</strong>
                        <span className="text-secondary">Não Informado</span>
                      </div>
                    )}
                  </span>
                  <span>
                    <strong>Criado em:</strong>{" "}
                    {chamadoAtual?.horariO_CRIADO != null
                      ? new Date(chamadoAtual.horariO_CRIADO)
                          .toLocaleString("pt-BR", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                          .replace(",", " às")
                      : "N/A"}
                  </span>
                </div>
              </div>
            </Modal.Title>
          </Modal.Header>

          <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
            <div>
              <label htmlFor="" className="fw-semibold">
                Empresa:
              </label>
              <p className="text-secondary">{chamadoAtual?.empresa}</p>

              <label htmlFor="" className="fw-semibold">
                Email:
              </label>
              <p className="text-secondary">{chamadoAtual?.email}</p>

              <label htmlFor="" className="fw-semibold">
                Telefone:
              </label>
              <p className="text-secondary">{chamadoAtual?.telefone}</p>

              <label htmlFor="" className="fw-semibold">
                Solicitação:
              </label>
              <p className="text-secondary">{chamadoAtual?.tipoSolicitacao}</p>

              <label htmlFor="" className="fw-semibold">
                Descrição:
              </label>
              <p className="text-secondary">{chamadoAtual?.descricao}</p>

              <label htmlFor="" className="fw-semibold">
                Respostas:
              </label>

              {chamadoAtual?.respostas && chamadoAtual.respostas.length > 0 ? (
                <div
                  className="historico-respostas"
                  style={{
                    border: "1px solid #eee",
                    borderRadius: "8px",
                    padding: "10px",
                  }}
                >
                  {chamadoAtual.respostas.map((resp) => (
                    <div key={resp.id} className="mb-3 border-bottom pb-2">
                      <p className="text-secondary mb-1">
                        {resp.textO_RESPOSTA}
                      </p>
                      <small className="text-muted">
                        Respondido por: {resp.nomE_USUARIO_RESPOSTA} em{" "}
                        {new Date(resp.datA_RESPOSTA).toLocaleString("pt-BR", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </small>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-danger">Nenhuma resposta encontrada.</p>
              )}

              {chamadoAtual?.status !== "Encerrado" &&
                chamadoAtual?.respostas &&
                chamadoAtual.respostas.length > 0 && (
                  <div className="mt-4">
                    <label
                      htmlFor="user-resposta"
                      className="form-label fw-semibold"
                    >
                      Sua Resposta:
                    </label>
                    <textarea
                      id="user-resposta"
                      className="form-control"
                      style={{ resize: "none" }}
                      value={resposta}
                      onChange={(e) => setResposta(e.target.value)}
                      rows={4}
                      placeholder="Digite sua resposta aqui..."
                    ></textarea>
                  </div>
                )}
            </div>
          </Modal.Body>

          <Modal.Footer>
            <button
              className="btn btn-outline-secondary"
              onClick={() => setModalUserShow(false)}
            >
              Fechar
            </button>
            {chamadoAtual?.status !== "Encerrado" &&
              chamadoAtual?.respostas &&
              chamadoAtual.respostas.length > 0 && (
                <button
                  className="btn btn-primary"
                  onClick={handleResponderChamado}
                >
                  Devolver Resposta
                </button>
              )}
          </Modal.Footer>
        </Modal>
      </div>
    );
  } else {
    async function handleEncerrarChamado(
      chamadoAtual: Chamado | null,
    ): Promise<void> {
      if (chamadoAtual?.status === "Encerrado") {
        toast.error("O chamado ja foi encerrado.");
        return;
      }

      if (!chamadoAtual) {
        return;
      }

      const confirmacao = window.confirm(
        "Tem certeza de que deseja encerrar o chamado?",
      );
      if (!confirmacao) {
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API}/api/chamado/encerrar/${chamadoAtual?.uuid}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.ok) {
        toast.success("Chamado encerrado com sucesso.");
      } else {
        toast.error("Erro ao encerrar o chamado.");
      }
    }

    return (
      <div className="container mt-5">
        <div className="d-flex align-items-center">
          <ChevronLeft
            className="me-2"
            size={24}
            style={{ cursor: "pointer" }}
            onClick={() => navigate(-1)}
          />
          <h2
            className="text-primary fw-bold"
            style={{ lineHeight: "0", padding: "0" }}
          >
            Lista de Chamados
          </h2>
        </div>

        <div className="d-flex justify-content-center justify-content-md-start justify-content-lg-start justify-content-xl-start flex-wrap gap-3 mt-4">
          {Array.isArray(chamados) &&
            [...chamados]
              .sort((a, b) => {
                const statusOrder: { [key: string]: number } = {
                  Pendente: 1,
                  Respondido: 2,
                  Encerrado: 3,
                };

                const statusA = statusOrder[a.status] || 4;
                const statusB = statusOrder[b.status] || 4;

                if (statusA !== statusB) {
                  return statusA - statusB;
                }

                return (
                  new Date(b.horariO_CRIADO).getTime() -
                  new Date(a.horariO_CRIADO).getTime()
                );
              })
              .map((chamado) => (
                <div
                  key={chamado.uuid}
                  className="card d-flex flex-column justify-content-between shadow-sm border-0 rounded-4"
                  style={{
                    width: "19rem",
                    backgroundColor: "#f8f9fa",
                    padding: "10px",
                    whiteSpace: "nowrap",
                  }}
                >
                  <div className="d-flex mb-2 justify-content-between">
                    <div className="d-flex flex-column">
                      <span className="fw-semibold">Chamado:</span>
                      <span className="mb-2 text-sm">
                        {chamado.num_Chamado
                          ? `#${chamado.num_Chamado}`
                          : "N/A"}
                      </span>

                      <span className="fw-semibold">Criado em:</span>
                      <span className="text-sm">
                        {chamado?.horariO_CRIADO
                          ? new Date(chamado.horariO_CRIADO)
                              .toLocaleString("pt-BR", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                              .replace(",", " às")
                          : "N/A"}
                      </span>
                    </div>

                    <div className="ms-2 d-flex align-items-start justify-content-start">
                      {chamado.status === "Pendente" && (
                        <div
                          className="d-flex align-items-center gap-2"
                          style={{
                            backgroundColor: "#f3f3d9",
                            padding: "8px",
                            borderRadius: "8px",
                          }}
                        >
                          <span
                            style={{
                              background: "orange",
                              width: "10px",
                              height: "10px",
                              borderRadius: "50%",
                            }}
                          ></span>
                          <span className="fw-semibold text-warning">
                            Pendente
                          </span>
                        </div>
                      )}
                      {chamado.status === "Respondido" && (
                        <div
                          className="d-flex align-items-center gap-2"
                          style={{
                            backgroundColor: "#d4edda",
                            padding: "8px",
                            borderRadius: "8px",
                          }}
                        >
                          <span
                            style={{
                              background: "limegreen",
                              width: "10px",
                              height: "10px",
                              borderRadius: "50%",
                            }}
                          ></span>
                          <span className="fw-semibold text-success">
                            Respondido
                          </span>
                        </div>
                      )}
                      {chamado.status === "Encerrado" && (
                        <div
                          className="d-flex align-items-center gap-2"
                          style={{
                            backgroundColor: "#edd4d4",
                            padding: "8px",
                            borderRadius: "8px",
                          }}
                        >
                          <span
                            style={{
                              background: "red",
                              width: "10px",
                              height: "10px",
                              borderRadius: "50%",
                            }}
                          ></span>
                          <span className="fw-semibold text-danger">
                            Encerrado
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <hr className="my-2" />

                  <div
                    className="text-muted text-sm"
                    style={{ overflow: "hidden" }}
                  >
                    <p>
                      <strong>Empresa:</strong> {chamado.empresa}
                    </p>
                    <p>
                      <strong>Email:</strong> {chamado.email}
                    </p>
                    <p>
                      <strong>Telefone:</strong> {chamado.telefone}
                    </p>
                    <p>
                      <strong>Solicitação:</strong> {chamado.tipoSolicitacao}
                    </p>
                    <p
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      <strong>Descrição:</strong> {chamado.descricao}
                    </p>
                  </div>

                  <div className="d-flex flex-column gap-2 mt-2">
                    <button
                      className="btn btn-outline-primary d-flex align-items-center justify-content-center gap-2"
                      onClick={() => {
                        setModalShow(true);
                        setChamadoAtual(chamado);
                      }}
                    >
                      {chamado.respostas.length > 0 ? "Analisar" : "Responder"}
                    </button>
                  </div>
                </div>
              ))}
        </div>

        <Modal
          show={modalShow}
          onHide={() => setModalShow(false)}
          centered
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <div className="d-flex flex-column">
                <strong>Responder Chamado</strong>
                <div className="d-flex align-items-center gap-3 mt-1 text-muted fs-6">
                  <span>
                    <strong>Chamado:</strong>{" "}
                    {chamadoAtual?.num_Chamado || "N/A"}
                  </span>
                  <span>
                    <strong>Criado em:</strong>{" "}
                    {chamadoAtual?.horariO_CRIADO
                      ? new Date(chamadoAtual.horariO_CRIADO)
                          .toLocaleString("pt-BR", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                          .replace(",", " às")
                      : "N/A"}
                  </span>
                </div>
              </div>
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <div className="d-flex flex-column gap-2">
              <strong>Descrição do Chamado:</strong>

              <p className="paragraph text-muted">{chamadoAtual?.descricao}</p>

              <label htmlFor="" className="form-label fw-semibold">
                Resposta(s):
              </label>

              <div>
                {chamadoAtual?.respostas.map((resp) => (
                  <div key={resp.id} className="mb-3 border-bottom pb-2">
                    <p className="text-secondary mb-1">{resp.textO_RESPOSTA}</p>
                    <small className="text-muted">
                      Respondido por: {resp.nomE_USUARIO_RESPOSTA} em{" "}
                      {new Date(resp.datA_RESPOSTA).toLocaleString("pt-BR", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </small>
                  </div>
                ))}
                {(!chamadoAtual?.respostas ||
                  chamadoAtual.respostas.length === 0) && (
                  <p className="text-muted mt-2">
                    Nenhuma resposta encontrada.
                  </p>
                )}
              </div>
            </div>
            <form className="form">
              {chamadoAtual?.status !== "Encerrado" && (
                <>
                  <label htmlFor="resposta" className="form-label">
                    Responda o Chamado:
                  </label>
                  <textarea
                    className="form-control"
                    title={""}
                    style={{ resize: "none" }}
                    value={resposta}
                    onChange={(e) => setResposta(e.target.value)}
                    name="resposta"
                    id=""
                  ></textarea>
                </>
              )}
            </form>
          </Modal.Body>

          <Modal.Footer>
            <button
              className="btn btn-outline-secondary"
              onClick={() => {
                setModalShow(false);
                setModalRelatoriosShow(true);
              }}
            >
              Relatorios
            </button>

            <button
              className="btn btn-secondary"
              onClick={() => handleEncerrarChamado(chamadoAtual)}
            >
              Encerrar Chamado
            </button>

            <button
              className="btn btn-primary"
              onClick={handleResponderChamado}
            >
              Responder
            </button>

            <button
              className="btn btn-danger"
              onClick={() => setModalShow(false)}
            >
              Fechar
            </button>
          </Modal.Footer>
        </Modal>

        <Modal
          show={modalRelatoriosShow}
          onHide={() => {
            setModalRelatoriosShow(false);
            setModalShow(true);
          }}
          centered
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>Relatórios Enviados</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {chamadoAtual?.relatorios &&
              (chamadoAtual.relatorios.length > 0 ? (
                <div
                  className="table-responsive"
                  style={{
                    display: "block",
                    maxHeight: "200px",
                    overflowY: "auto",
                  }}
                >
                  <table className="table table-striped table-hover">
                    <thead>
                      <tr>
                        <th>Nome do Arquivo</th>
                        <th>Data de Envio</th>
                        <th>Enviado Por</th>
                      </tr>
                    </thead>
                    <tbody>
                      {chamadoAtual.relatorios.map((relatorio, index) => (
                        <tr key={index}>
                          <td>
                            <button
                              className="btn btn-link"
                              onClick={() =>
                                handleDownload(
                                  relatorio.caminho_arquivo,
                                  relatorio.nome_arquivo,
                                )
                              }
                            >
                              {relatorio.nome_arquivo}
                            </button>
                          </td>
                          <td>
                            {new Date(relatorio.data_upload)
                              .toLocaleString()
                              .replace(",", " às ")}
                          </td>
                          <td>{relatorio.enviador || "Desconhecido"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center">Nenhum relatório enviado</p>
              ))}
          </Modal.Body>
          <Modal.Footer>
            {chamadoAtual && (
              <>
                <input
                  type="file"
                  title={""}
                  id="inputRelatorio"
                  style={{ display: "none" }}
                  onChange={(e) =>
                    handleUploadChamadoRelatorio(chamadoAtual, e)
                  }
                  accept=".pdf,.doc,.docx"
                />

                <button
                  className="btn btn-primary"
                  onClick={() =>
                    document.getElementById("inputRelatorio")?.click()
                  }
                >
                  {isLoading ? <Loading color="#fff" /> : "Enviar Relatório"}
                </button>
              </>
            )}
            <button
              className="btn btn-danger"
              onClick={() => {
                setModalRelatoriosShow(false);
                setModalShow(true);
              }}
            >
              Fechar
            </button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
};

export default Chamados;
